import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Callout, FormField, Input, Modal, Select } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import { useCreateAppRoleForm, type AppRoleTargetApp } from './useCreateAppRoleForm';

export interface CreateAppRoleModalProps {
    /** The app to create the role in — `null` keeps the modal closed. Only ever
     *  passed for an app whose `supports_role_management` is true. */
    app: AppRoleTargetApp | null;
    onClose: () => void;
    /** Fired after a successful create, with the created row and the org it was
     *  created for — lets a host surface pre-select it (see `RoleMapAddModal`). */
    onCreated?: (role: AppRoleOption, organizationId: string) => void;
}

/**
 * EAA-1841: "Tạo vai trò trong ứng dụng" — one modal shared by all three places
 * an operator notices a role is missing: the registry row menu (`/console/apps`),
 * the link flow (`RoleMapAddModal`) and the coverage editor
 * (`/console/role-coverage`).
 *
 * Deliberately NOT a permissions editor. `POST /admin/app-roles` forwards to the
 * app's own receiver, which refuses unknown permission codes outright, and
 * nothing exposes an app's valid codes to this console — so the role is created
 * empty (granting nothing, which is the safe state) and its permissions are set
 * inside the app. The callout below says so rather than leaving the operator to
 * wonder where the permissions field went.
 *
 * The organization field is required by the server, not padding: the role lands
 * in an app-local scope resolved from the org, and the created row is org-scoped
 * — which is why the copy names the org it will belong to.
 */
export function CreateAppRoleModal({ app, onClose, onCreated }: CreateAppRoleModalProps) {
    const { t } = useTranslation();
    const vm = useCreateAppRoleForm(app, (role, organizationId) => {
        onCreated?.(role, organizationId);
        onClose();
    });

    // EAA-1817: kit `Select` options built once per org list change rather than
    // inline in JSX, so the array stays referentially stable across renders.
    const orgOptions = useMemo(
        () => vm.orgs.map((org) => ({ value: org.id, label: `${org.code} — ${org.name}` })),
        [vm.orgs]
    );

    const appName = app?.name ?? t('console.appRoles.createModal.genericAppFallback');

    function handleClose() {
        if (vm.pending) return;
        onClose();
    }

    function fieldError(key: string): string | null {
        return vm.fieldErrors[key]?.[0] ?? null;
    }

    return (
        <Modal
            open={vm.open}
            onCancel={handleClose}
            title={t('console.appRoles.createModal.title', { appName })}
            width={560}
            footer={
                <>
                    <Button onClick={handleClose} disabled={vm.pending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form="portal-app-role-create-form"
                        disabled={!vm.canSubmit || vm.pending}
                    >
                        {vm.pending
                            ? t('console.appRoles.createModal.creating')
                            : t('console.appRoles.createModal.submitButton')}
                    </Button>
                </>
            }
        >
            <form
                id="portal-app-role-create-form"
                className="portal-admin-form"
                style={{ gridTemplateColumns: '1fr' }}
                onSubmit={vm.onSubmit}
            >
                <p className="portal-admin-hint">
                    <Trans
                        i18nKey="console.appRoles.createModal.hint"
                        values={{ appName: app?.name }}
                        components={{ bold: <strong /> }}
                    />
                </p>

                <FormField
                    label={t('console.appRoles.createModal.organizationLabel')}
                    hint={
                        vm.orgsLoading
                            ? t('console.appRoles.createModal.organizationLoadingHint')
                            : vm.orgOverflow
                              ? // EAA-1841: the picker holds one page. Saying so is the
                                // only honest option — silently truncating would read as
                                // "that org does not exist".
                                t('console.appRoles.createModal.organizationOverflowHint', {
                                    count: vm.orgs.length,
                                })
                              : undefined
                    }
                    error={
                        fieldError('organization_id') ??
                        (vm.orgsError ? t('console.appRoles.createModal.organizationErrorHint') : undefined)
                    }
                >
                    {/* EAA-1817: consolidates the raw organization <select> AND the
                        separate "Tìm tổ chức" search <input> that stood next to it
                        into one kit `Select` — `showSearch` gives it a filterable
                        dropdown, so the second field is no longer needed at all.
                        `onSearch` (not `filterOption`) drives `vm.setOrgSearch`
                        because the org list is server-paginated and already
                        debounced/filtered by `useCreateAppRoleForm`'s query — antd's
                        own client-side option filtering is disabled so it cannot
                        hide a server match that doesn't happen to match locally. */}
                    <Select
                        value={vm.organizationId || undefined}
                        onChange={(value) => vm.setOrganizationId((value as string) ?? '')}
                        onSearch={vm.setOrgSearch}
                        options={orgOptions}
                        loading={vm.orgsLoading}
                        showSearch
                        filterOption={false}
                        placeholder={t('console.appRoles.createModal.organizationPlaceholder')}
                        style={{ width: '100%' }}
                    />
                </FormField>

                <FormField
                    label={t('console.appRoles.createModal.keyLabel')}
                    // EAA-1841: the key is what provisioning pushes back to the app
                    // verbatim, so its immutability matters more here than for a
                    // display name — state it at the point of entry.
                    hint={t('console.appRoles.createModal.keyHint')}
                    error={vm.keyError ?? fieldError('key') ?? fieldError('slug')}
                >
                    <Input
                        value={vm.form.key}
                        onChange={(event) => vm.setField('key', event.target.value)}
                        onBlur={vm.onKeyBlur}
                        placeholder={t('console.appRoles.createModal.keyPlaceholder')}
                        required
                    />
                </FormField>

                <FormField label={t('console.appRoles.createModal.nameLabel')} error={fieldError('name')}>
                    <Input
                        value={vm.form.name}
                        onChange={(event) => vm.setField('name', event.target.value)}
                        placeholder={t('console.appRoles.createModal.namePlaceholder')}
                        required
                    />
                </FormField>

                <FormField
                    label={t('console.appRoles.createModal.descriptionLabel')}
                    error={fieldError('description')}
                >
                    <Input.TextArea
                        value={vm.form.description}
                        onChange={(event) => vm.setField('description', event.target.value)}
                        rows={2}
                    />
                </FormField>

                <Callout
                    tone="info"
                    label={t('console.appRoles.createModal.calloutLabel')}
                    longLabel
                >
                    <Trans
                        i18nKey="console.appRoles.createModal.calloutBody"
                        values={{ appName: app?.name }}
                    />
                </Callout>

                {vm.formError && <p className="portal-admin-error">{vm.formError}</p>}
            </form>
        </Modal>
    );
}

export default CreateAppRoleModal;
